import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Badge } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { UserModal } from '@/components/admin/UserModal';
import { DeleteConfirmModal } from '@/components/admin/DeleteConfirmModal';
import { SuccessModal } from '@/components/admin/SuccessModal';
import { ConfirmModal } from '@/components/admin/ConfirmModal';
import { useAuth } from '@/contexts/AuthContext';
import {
  auth,
  authSecondary,
  db,
  collection,
  getDocs,
  query,
  orderBy,
  callFunction,
  doc,
  setDoc,
  updateDoc,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  serverTimestamp,
} from '@/lib/firebase';
import { formatDate } from '@/lib/firebase';
import type { User, CreateUserData } from '@/types';
import {
  UserPlus,
  Search,
  Ban,
  CheckCircle,
  Trash2,
  Shield,
  Mail,
  RefreshCw,
  MoreVertical,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const UserManagement: React.FC = () => {
  const { user: currentUser, refreshUserData } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState<'all' | 'users' | 'admins'>('all');
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [reactivateModalOpen, setReactivateModalOpen] = useState(false);
  const [resendEmailModalOpen, setResendEmailModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: '', message: '' });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const isSuperAdmin = currentUser?.role === 'superadmin';

  // Load users
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() } as User)
      );
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Create user
  const handleCreateUser = async (userData: CreateUserData) => {
    try {
      const tempPassword = Math.random().toString(36).slice(-12) + 'A1!';

      const userCredential = await createUserWithEmailAndPassword(
        authSecondary,
        userData.email,
        tempPassword
      );

      const userDoc = {
        email: userData.email,
        displayName: userData.displayName,
        role: userData.role,
        status: 'pending',
        createdAt: serverTimestamp(),
        passwordResetSentAt: serverTimestamp(),
        lastLogin: null,
      };

      await setDoc(doc(db, 'users', userCredential.user.uid), userDoc);
      await signOut(authSecondary);

      try {
        await sendPasswordResetEmail(auth, userData.email);
        setSuccessMessage({
          title: 'Utente Creato!',
          message: `Email inviata a ${userData.email} per impostare la password.`
        });
        setSuccessModalOpen(true);
        await loadUsers();
      } catch (emailError) {
        console.error('Error sending password setup email:', emailError);
        throw new Error('Utente creato, ma errore nell\'invio dell\'email. Usa "Reset Password" per reinviare.');
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      throw new Error(error.message || 'Errore durante la creazione dell\'utente');
    }
  };

  // Suspend user
  const handleSuspendUser = async () => {
    if (!selectedUser) return;

    try {
      setActionLoading(true);
      const suspendUser = callFunction<{ userId: string }, any>('suspendUser');
      await suspendUser({ userId: selectedUser.id });
      setSuspendModalOpen(false);
      setSuccessMessage({
        title: 'Utente Sospeso!',
        message: `L'utente ${selectedUser.displayName} è stato sospeso con successo.`
      });
      setSuccessModalOpen(true);
      await loadUsers();
      await refreshUserData();
      setSelectedUser(null);
    } catch (error) {
      console.error('Error suspending user:', error);
      setSuspendModalOpen(false);
      setSuccessMessage({
        title: 'Errore',
        message: 'Errore durante la sospensione dell\'utente. Riprova.'
      });
      setSuccessModalOpen(true);
    } finally {
      setActionLoading(false);
    }
  };

  // Reactivate user
  const handleReactivateUser = async () => {
    if (!selectedUser) return;

    try {
      setActionLoading(true);
      const reactivateUser = callFunction<{ userId: string }, any>('reactivateUser');
      await reactivateUser({ userId: selectedUser.id });
      setReactivateModalOpen(false);
      setSuccessMessage({
        title: 'Utente Riattivato!',
        message: `L'utente ${selectedUser.displayName} è stato riattivato con successo.`
      });
      setSuccessModalOpen(true);
      await loadUsers();
      await refreshUserData();
      setSelectedUser(null);
    } catch (error) {
      console.error('Error reactivating user:', error);
      setReactivateModalOpen(false);
      setSuccessMessage({
        title: 'Errore',
        message: 'Errore durante la riattivazione dell\'utente. Riprova.'
      });
      setSuccessModalOpen(true);
    } finally {
      setActionLoading(false);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setActionLoading(true);
      const deleteUserFunction = callFunction<{ userId: string }, any>('deleteUser');
      await deleteUserFunction({ userId: selectedUser.id });
      await loadUsers();
      setDeleteModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Errore durante l\'eliminazione dell\'utente');
    } finally {
      setActionLoading(false);
    }
  };

  // Resend activation email
  const handleResendEmail = async () => {
    if (!selectedUser) return;

    try {
      setActionLoading(true);

      await sendPasswordResetEmail(auth, selectedUser.email);

      await updateDoc(doc(db, 'users', selectedUser.id), {
        passwordResetSentAt: serverTimestamp(),
        status: 'pending'
      });

      setResendEmailModalOpen(false);
      setSuccessMessage({
        title: 'Email Inviata!',
        message: `Nuovo link di attivazione inviato a ${selectedUser.email}. Il link precedente non è più valido.`
      });
      setSuccessModalOpen(true);
      await loadUsers();
      setSelectedUser(null);
    } catch (error) {
      console.error('Error resending email:', error);
      setResendEmailModalOpen(false);
      setSuccessMessage({
        title: 'Errore',
        message: 'Errore durante l\'invio dell\'email. Riprova.'
      });
      setSuccessModalOpen(true);
    } finally {
      setActionLoading(false);
    }
  };

  // Filter users
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesTab =
      selectedTab === 'all' ||
      (selectedTab === 'users' && user.role === 'user') ||
      (selectedTab === 'admins' && (user.role === 'admin' || user.role === 'superadmin'));

    return matchesSearch && matchesTab;
  });

  // Stats
  const stats = {
    totalUsers: users.length,
    activeUsers: users.filter((u) => u.status === 'active').length,
    regularUsers: users.filter((u) => u.role === 'user').length,
    admins: users.filter((u) => u.role === 'admin' || u.role === 'superadmin').length,
  };

  // User Actions Component
  const UserActions: React.FC<{ user: User }> = ({ user }) => {
    const isOpen = openDropdown === user.id;

    return (
      <div className="relative">
        {/* Desktop Actions - inline buttons */}
        <div className="hidden md:flex items-center gap-2">
          {(user.status === 'pending' || user.status === 'expired') && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSelectedUser(user);
                setResendEmailModalOpen(true);
              }}
              disabled={actionLoading}
              icon={<Mail className="w-4 h-4 text-blue-600" />}
            >
              Reinvia Email
            </Button>
          )}
          {user.status === 'active' && user.id !== currentUser?.id && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSelectedUser(user);
                setSuspendModalOpen(true);
              }}
              disabled={actionLoading}
              icon={<Ban className="w-4 h-4" />}
            >
              Sospendi
            </Button>
          )}
          {user.status === 'suspended' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSelectedUser(user);
                setReactivateModalOpen(true);
              }}
              disabled={actionLoading}
              icon={<CheckCircle className="w-4 h-4" />}
            >
              Riattiva
            </Button>
          )}
          {isSuperAdmin && user.id !== currentUser?.id && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSelectedUser(user);
                setDeleteModalOpen(true);
              }}
              disabled={actionLoading}
              icon={<Trash2 className="w-4 h-4 text-red-600" />}
            >
              Elimina
            </Button>
          )}
        </div>

        {/* Mobile Actions - dropdown menu */}
        <div className="md:hidden">
          <button
            onClick={() => setOpenDropdown(isOpen ? null : user.id)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <MoreVertical className="w-5 h-5 text-gray-600" />
          </button>
          <AnimatePresence>
            {isOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setOpenDropdown(null)}
                />
                {/* Dropdown */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden"
                >
                  {(user.status === 'pending' || user.status === 'expired') && (
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setResendEmailModalOpen(true);
                        setOpenDropdown(null);
                      }}
                      disabled={actionLoading}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <Mail className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium">Reinvia Email</span>
                    </button>
                  )}
                  {user.status === 'active' && user.id !== currentUser?.id && (
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setSuspendModalOpen(true);
                        setOpenDropdown(null);
                      }}
                      disabled={actionLoading}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <Ban className="w-4 h-4 text-orange-600" />
                      <span className="text-sm font-medium">Sospendi</span>
                    </button>
                  )}
                  {user.status === 'suspended' && (
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setReactivateModalOpen(true);
                        setOpenDropdown(null);
                      }}
                      disabled={actionLoading}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium">Riattiva</span>
                    </button>
                  )}
                  {isSuperAdmin && user.id !== currentUser?.id && (
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setDeleteModalOpen(true);
                        setOpenDropdown(null);
                      }}
                      disabled={actionLoading}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-red-50 transition-colors disabled:opacity-50 border-t border-gray-200"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                      <span className="text-sm font-medium text-red-600">Elimina</span>
                    </button>
                  )}
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  };

  // User Card Component for mobile
  const UserCard: React.FC<{ user: User }> = ({ user }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
    >
      {/* User Info */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 truncate">{user.displayName}</p>
          <p className="text-sm text-gray-500 truncate">{user.email}</p>
        </div>
        <UserActions user={user} />
      </div>

      {/* Badges */}
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant={user.role === 'superadmin' ? 'danger' : user.role === 'admin' ? 'warning' : 'default'}>
          {user.role === 'superadmin' && <Shield className="w-3 h-3 mr-1" />}
          {user.role}
        </Badge>
        <Badge
          variant={
            user.status === 'active'
              ? 'success'
              : user.status === 'suspended'
              ? 'danger'
              : user.status === 'expired'
              ? 'danger'
              : 'warning'
          }
        >
          {user.status === 'pending' && <Mail className="w-3 h-3 mr-1" />}
          {user.status === 'expired' && <RefreshCw className="w-3 h-3 mr-1" />}
          {user.status}
        </Badge>
      </div>

      {/* Details */}
      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
        <div>
          <span className="font-medium">Creato:</span>{' '}
          <span>{formatDate(user.createdAt)}</span>
        </div>
        <div>
          <span className="font-medium">Ultimo accesso:</span>{' '}
          <span>{user.lastLogin ? formatDate(user.lastLogin) : 'Mai'}</span>
        </div>
        {(user.status === 'pending' || user.status === 'expired') && user.passwordResetSentAt && (
          <div className="col-span-2">
            <span className="font-medium">Link attivazione:</span>{' '}
            <span>{formatDate(user.passwordResetSentAt)}</span>
          </div>
        )}
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestione Utenti</h1>
            <p className="text-gray-600 mt-1">
              Gestisci tutti gli utenti del sistema ({stats.totalUsers} totali, {stats.activeUsers} attivi)
            </p>
          </div>
          <Button
            variant="primary"
            icon={<UserPlus className="w-5 h-5" />}
            onClick={() => setUserModalOpen(true)}
            className="w-full sm:w-auto"
          >
            Nuovo Utente
          </Button>
        </div>

        {/* Users List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Elenco Utenti</CardTitle>
                <CardDescription>Visualizza e gestisci tutti gli utenti del sistema</CardDescription>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-4 mt-4">
              {/* Search */}
              <Input
                icon={Search}
                placeholder="Cerca per nome o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              {/* Tabs */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={selectedTab === 'all' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedTab('all')}
                  className="flex-1 sm:flex-initial"
                >
                  Tutti ({users.length})
                </Button>
                <Button
                  variant={selectedTab === 'users' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedTab('users')}
                  className="flex-1 sm:flex-initial"
                >
                  Utenti ({stats.regularUsers})
                </Button>
                <Button
                  variant={selectedTab === 'admins' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedTab('admins')}
                  className="flex-1 sm:flex-initial"
                >
                  Admin ({stats.admins})
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Utente</TableHead>
                    <TableHead>Ruolo</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Creato</TableHead>
                    <TableHead>Ultimo Accesso</TableHead>
                    <TableHead>Link Attivazione</TableHead>
                    <TableHead>Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        Nessun utente trovato
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div>
                            <p className="font-semibold text-gray-900">{user.displayName}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.role === 'superadmin' ? 'danger' : user.role === 'admin' ? 'warning' : 'default'}>
                            {user.role === 'superadmin' && <Shield className="w-3 h-3 mr-1" />}
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              user.status === 'active'
                                ? 'success'
                                : user.status === 'suspended'
                                ? 'danger'
                                : user.status === 'expired'
                                ? 'danger'
                                : 'warning'
                            }
                          >
                            {user.status === 'pending' && <Mail className="w-3 h-3 mr-1" />}
                            {user.status === 'expired' && <RefreshCw className="w-3 h-3 mr-1" />}
                            {user.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {formatDate(user.createdAt)}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {user.lastLogin ? formatDate(user.lastLogin) : 'Mai'}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {(user.status === 'pending' || user.status === 'expired') && user.passwordResetSentAt
                            ? formatDate(user.passwordResetSentAt)
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <UserActions user={user} />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nessun utente trovato
                </div>
              ) : (
                filteredUsers.map((user) => <UserCard key={user.id} user={user} />)
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <UserModal
        isOpen={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        onSubmit={handleCreateUser}
        currentUserRole={currentUser?.role || 'user'}
      />

      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleDeleteUser}
        title="Elimina Utente"
        message="Sei sicuro di voler eliminare questo utente? Tutti i suoi dati verranno rimossi."
        itemName={selectedUser?.displayName}
        loading={actionLoading}
      />

      <ConfirmModal
        isOpen={suspendModalOpen}
        onClose={() => {
          setSuspendModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleSuspendUser}
        title="Sospendi Utente"
        message={`Sei sicuro di voler sospendere ${selectedUser?.displayName}? L'utente non potrà più accedere all'applicazione.`}
        confirmText="Sospendi"
        cancelText="Annulla"
        loading={actionLoading}
        variant="warning"
      />

      <ConfirmModal
        isOpen={reactivateModalOpen}
        onClose={() => {
          setReactivateModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleReactivateUser}
        title="Riattiva Utente"
        message={`Vuoi riattivare ${selectedUser?.displayName}? L'utente potrà nuovamente accedere all'applicazione.`}
        confirmText="Riattiva"
        cancelText="Annulla"
        loading={actionLoading}
        variant="info"
      />

      <ConfirmModal
        isOpen={resendEmailModalOpen}
        onClose={() => {
          setResendEmailModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleResendEmail}
        title="Reinvia Email di Attivazione"
        message={`Vuoi inviare una nuova email di attivazione a ${selectedUser?.email}? Il link precedente non sarà più valido.`}
        confirmText="Invia Email"
        cancelText="Annulla"
        loading={actionLoading}
        variant="info"
      />

      <SuccessModal
        isOpen={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        title={successMessage.title}
        message={successMessage.message}
        autoCloseMs={3000}
      />
    </AdminLayout>
  );
};
